import React, { forwardRef, useState, useEffect } from 'react';
import './../css/Experience.css';

const experienceData = [
  {
    type: 'work',
    title: 'Research Assistant',
    org: 'University of Michigan-Dearborn',
    location: 'Dearborn, Michigan',
    date: 'May 2025 – Present',
    details: [
      'Developed benchmark validation datasets (GitHub) in C++/Python to evaluate scheduling optimization.',
      'Analyzed system logs and runtime performance via Bash and ‘perf‘ to identify efficiency bottlenecks.',
      'Integrated Agents Like MSWE, MOpenHands and MAgentLess to optimize the benchmarks, Authored structured QA tests and reproducibility documentation, increasing optimization by 46%.'
    ]
  },
  {
    type: 'work',
    title: 'Software Developer Intern (React.js, Node.js, REST)',
    org: 'GoMicro – AI-based Quality Analysis Company',
    location: '',
    date: 'June 2025 – Present',
    details: [
      'Built real-time AI dashboards processing 100+ image predictions/day.',
      'Integrated Dashboard classifiers with Node.js APIs and React frontend with live updates.'
    ]
  },
  {
    type: 'work',
    title: 'Technical CoFounder & Full Stack Developer',
    org: 'ThePortfolyo',
    location: 'Remote',
    date: 'Dec 2023 – May 2025',
    details: [
      'Co-developed an AI-powered portfolio generator using Node.js, React, MongoDB, and OpenAI, converting resumes into interactive portfolios within seconds (GitHub).',
      'Deployed microservices to AWS EC2, managed CI/CD via GitHub Actions, and implemented secure S3 file handling.',
      'Led functional teams across front-end (React, Next.js), backend (Node.js), and design to optimize platform performance.',
      'Achieved 150+ successful deployments with lazy loading, SEO optimization, JWT/OAuth, and user dashboards.',
      'Built unit (Jest) and e2e (Cypress) tests; documented APIs with Swagger.',
      'Reduced portfolio creation time by 70% compared to traditional platforms.'
    ]
  },
  {
    type: 'work',
    title: 'Software Engineer',
    org: 'Webriy',
    location: 'Remote',
    date: 'Jan 2023 – Nov 2023',
    details: [
      'Contributed to the development of responsive and SEO-optimized websites for clients like BNZ India and Eventive Communications using WordPress and Webflow.',
      'Collaborated with UI/UX teams to deliver high-performance digital solutions, streamlining development by 15% through version control and MERN stack integration.'
    ]
  },
  {
    type: 'work',
    title: 'Software Developer Intern',
    org: 'Legal Seva',
    location: '',
    date: 'Aug 2022 – Jan 2023',
    details: []
  },
  {
    type: 'education',
    title: 'M.S. in Computer & Information Science & Current RA',
    org: 'University of Michigan-Dearborn',
    location: 'Dearborn, Michigan',
    date: 'Aug 2024 – May 2026',
    details: [
      'CGPA: 3.78',
      'Specialized in systems programming, software engineering, and QA methodologies.'
    ]
  },
  {
    type: 'education',
    title: 'B.Tech in Computer Science',
    org: 'University of Engineering and Management, Kolkata',
    location: 'Kolkata, India',
    date: 'Aug 2020 – May 2024',
    details: [
      'CGPA: 3.928',
      'Mastered core CS fundamentals including OS, DSA, Computer Networks, and AI/ML.',
      'Graduated as University Gold Medalist.'
    ]
  }
];

const skills = [
  'JavaScript (ES6+)', 'TypeScript', 'C', 'Python', 'HTML5', 'CSS3',
  'React.js', 'Angular', 'Redux', 'Tailwind CSS', 'WCAG Accessibility',
  'Node.js', 'Express.js', '.NET Core', 'REST APIs',
  'SQL Server', 'PostgreSQL', 'MongoDB', 'Mongoose', 'Stored Procedures',
  'Clean Code', 'System Design', 'OOP', 'MVC', 'Microservices', 'Agile (Scrum)',
  'GitHub Actions', 'Docker', 'CI/CD', 'Azure DevOps', 'AWS EC2/S3', 'Jira',
  'Jest', 'Mocha', 'Selenium', 'Integration Testing', 'Unit Testing',
  'UX/PM Integration', 'Code Reviews', 'Cross-functional Agile Teams'
];

const certifications = [
  'AWS Certified Cloud Practitioner (CLF-C01) Cert Prep: 1 Cloud Concepts – LinkedIn Learning Jan 2024',
  'Practical GitHub Copilot – LinkedIn Learning Dec 2023',
  'Full Stack Web Development Apprenticeship – MyCaptain 2022',
  'Data Structures and Algorithms (Advanced) – Code with Mosh 2022'
];

const projects = [
  {
    title: 'ThePortfolyo, AI-Powered Full Stack Portfolio Generator',
    date: 'Dec 2023 – Present',
    details: [
      'Built a full-stack platform using Node.js, React, MongoDB to convert resumes into responsive portfolio websites.',
      'Integrated OpenAI API via FastAPI and LangChain; deployed on AWS EC2 with S3 integration and GitHub Actions CI/CD (GitHub).',
      'Optimized page load speed by 38% with caching, lazy loading, and frontend rendering improvements.'
    ]
  },
  {
    title: 'Systems + AI Research Project Under Prof. Roy, Optimization Benchmarking Suite',
    date: 'Apr 2025 – Present',
    details: [
      'Built reproducible benchmarking tools using C++ and Python to evaluate large-scale scheduling algorithms.',
      'Automated performance profiling via Docker, Bash, and ‘perf‘, achieving 46% runtime improvement and did MAgentLess Integration for Optimization of Benchmarks(GitHub).'
    ]
  },
  {
    title: 'CopyElement, Frontend Component Library for WordPress and Other Developers',
    date: 'Jan 2024 – Present',
    details: [
      'Developed reusable and accessible UI components with JavaScript, Next Js, CSS3, and deployed with GitLab CI/CD pipelines.'
    ]
  },
  {
    title: 'Legacy App Modernization (React + .NET), Full Stack Modernization of Compliance & Registration Tool',
    date: 'Mar 2025 – Jun 2025',
    details: [
      'Migrated monolithic ASP.NET WebForms application to modern React.js frontend and .NET Core backend.',
      'Designed normalized SQL Server schema; rewrote stored procedures and optimized query performance.',
      'Implemented secure RESTful APIs with .NET Core and C; used React for responsive UI and form validation.',
      'Collaborated with testers and UX teams in Agile sprints to deliver modular, maintainable architecture.',
      'Set up CI/CD using GitHub Actions and Docker; ensured 85% test coverage using Jest and Selenium.'
    ]
  }
];

const Experience = forwardRef((props, ref) => {
  const [activeTab, setActiveTab] = useState('work');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderTimeline = (type) => (
    <div className="timeline">
      {experienceData.filter(item => item.type === type).map((item, idx) => (
        <div className="timeline-item" key={idx}>
          <div className="timeline-dot" />
          <div className="timeline-content">
            <h3>{item.title}</h3>
            <h4>{item.org}{item.location && <span> | {item.location}</span>}</h4>
            <span className="timeline-date">{item.date}</span>
            <ul>
              {item.details.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section className="container experience-section" ref={ref}>
      <h1 className="mainHeading">EXPERIENCE & EDUCATION</h1>
      <div className="exp-tabs">
        <button className={activeTab==='work' ? 'active' : ''} onClick={()=>setActiveTab('work')}>Work</button>
        <button className={activeTab==='education' ? 'active' : ''} onClick={()=>setActiveTab('education')}>Education</button>
        <button className={activeTab==='skills' ? 'active' : ''} onClick={()=>setActiveTab('skills')}>Skills</button>
        <button className={activeTab==='certs' ? 'active' : ''} onClick={()=>setActiveTab('certs')}>Certifications</button>
      </div>
      <div className="exp-content">
        {activeTab==='work' && renderTimeline('work')}
        {activeTab==='education' && renderTimeline('education')}
        {activeTab==='skills' && (
          <div className="skills-list">
            {skills.map((skill, idx) => <span className="skill-badge" key={idx}>{skill}</span>)}
          </div>
        )}
        {activeTab==='certs' && (
          <ul className="cert-list">
            {certifications.map((cert, idx) => <li key={idx}>{cert}</li>)}
          </ul>
        )}
      </div>
    </section>
  );
});

export default Experience; 